import { MapPin, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import HtmlText from "@/components/HtmlText";
import SmartImage from "@/components/SmartImage";
import { TechChip } from "@/lib/tech";
import { articlePath } from "@/lib/radarPaths";
import { jobLabel, jobBadges, hasSalary } from "@/lib/jobLabels";

const JobCard = ({ item, idx = 0 }) => {
  const { t, lang } = useLang();
  const to = articlePath("jobs", item);
  return (
    <article
      data-testid={`job-card-${idx}`}
      className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors p-4 flex flex-col gap-2"
    >
      <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono-dl uppercase tracking-wider">
        {item.work_mode && (
          <span data-testid={`job-card-workmode-${idx}`} className="px-1.5 py-0.5 bg-[#111827] text-white">{jobLabel("work_mode", item.work_mode, lang)}</span>
        )}
        {jobBadges(item, lang).map((b, i) => (
          <span key={b.field} data-testid={`job-card-${b.field}-${idx}`} className="text-[#4B5563]">{i > 0 || item.work_mode ? "· " : ""}{b.label}</span>
        ))}
        <span className="ml-auto text-[#9ca3af]">{item.published_at}</span>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 shrink-0 border border-[#E5E7EB] bg-white grid place-items-center overflow-hidden">
          {item.company_logo ? (
            <SmartImage
              src={item.company_logo}
              type="job"
              alt={item.company || ""}
              className="max-w-[80%] max-h-[80%]"
              fit="contain"
            />
          ) : (
            <span className="font-display text-lg text-[#9ca3af]">
              {(item.company || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-[17px] leading-snug text-[#111827]">
            <Link
              to={to}
              data-testid={`job-title-${idx}`}
              className="group-hover:text-[#00A878] transition-colors"
            >
              {item.title}
            </Link>
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#4B5563] mt-1">
            {item.company && <span className="inline-flex items-center gap-1"><Building2 size={12} /> {item.company}</span>}
            {item.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {item.location}</span>}
          </div>
          {hasSalary(item.salary_range) && (
            <div data-testid={`job-card-salary-${idx}`} className="text-[12px] font-mono-dl text-[#00A878] mt-1">{item.salary_range}</div>
          )}
        </div>
      </div>
      {item.technologies && item.technologies.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {item.technologies.slice(0, 6).map((tech) => (
            <TechChip key={tech} name={tech} variant="soft" size="xs" />
          ))}
        </div>
      )}
      {item.dataleo_angle && (
        <div className="border-l-2 border-[#00A878] pl-3 mt-1">
          <div className="text-[10px] font-mono-dl tracking-wider uppercase text-[#00A878]">{t.labels.dataleoAngle}</div>
          <HtmlText html={item.dataleo_angle} className="text-[12px] text-[#111827] mt-0.5 line-clamp-2" />
        </div>
      )}
    </article>
  );
};

export default JobCard;
